/**
 * SurfaceRailNav — `nav.rail`, the second section of the spine.
 *
 * Built to `publisher-drop-hub.html` lines 966-999. Class names are the
 * anchor's (`nv`, `nv__i`, `nv__n`, `nv__s`, `nv__d`), so `HubSpine.scss`
 * carries it unchanged.
 *
 * ## The items DIVIDE the rail; they do not stack inside it
 *
 * `.rail` is `height:var(--band)` and every `.nv` is `flex:1;min-height:0`.
 * That pairing is the whole behaviour: the rail is exactly as tall as the
 * stage beside it, and the items share that height out between them. Seven
 * items or three, the rail stays flush with the stage. Give the items
 * intrinsic heights instead and a surface with four sections leaves a ragged
 * gap the stage does not have — which is the difference between a page that
 * fits and one that was laid out for a different amount of content.
 *
 * `min-height:0` is load-bearing rather than defensive: a flex item's default
 * `min-height:auto` refuses to shrink below its content, so one long label
 * pushes the rail past `--band` and breaks the alignment `flex:1` exists to
 * hold.
 *
 * ## It knows nothing about what it is navigating
 *
 * Items arrive as `{id, label, sublabel?, icon?, live?}`. No section type, no
 * POV, no host type. The join from a surface's sections to these lives in
 * `dropSlots.ts` — the one file allowed to speak both vocabularies — which is
 * what keeps `layerBoundary.test.ts` green and makes a tenth POV a preset
 * rather than a code change.
 *
 * ## Sublabels appear only where the surface has one
 *
 * The anchor's items each carry a second line ("What's live", "Beta → Oct 23",
 * "38 live now"). Those are authored or derived copy. Where a surface supplies
 * none, the item is a single line — the row still divides the rail correctly
 * because the height comes from `flex:1`, not from the content. Filling the
 * slot with a placeholder would put words on the page nobody wrote.
 */

import React from "react";

export interface SurfaceRailNavItem {
    /** Matches the slot id the structure positions, so selecting one is addressable. */
    id: string;
    label: string;
    /** Second line. Absent renders nothing — see above. */
    sublabel?: string;
    /** Supplied by the caller; this component never picks an icon by type. */
    icon?: React.ReactNode;
    /**
     * Draws `.nv__d`, the accent dot. It means "something is happening here",
     * which is not the same as "this is the item you are on" — the anchor's
     * first item carries both, and they are two different marks.
     */
    live?: boolean;
}

export interface SurfaceRailNavProps {
    items: readonly SurfaceRailNavItem[];
    /** The active item. Absent highlights nothing — it does not mean "the first". */
    activeId?: string | null;
    onSelect?: (id: string) => void;
    /** Accessible name for the landmark. */
    label?: string;
}

export const SurfaceRailNav: React.FC<SurfaceRailNavProps> = ({
    items, activeId, onSelect, label = "Sections",
}) => {
    /*
     * No items, no rail. Rendering the box empty is what the structure did
     * before this existed: a correctly-sized 246px panel with nothing in it
     * down the left of the page. An absent slot is a configuration; an empty
     * one is a bug that looks like a design.
     */
    if (items.length === 0) return null;

    return (
        <nav className="sh-rail" aria-label={label}>
            {items.map(item => {
                const active = item.id === activeId;
                return (
                    <button
                        key={item.id}
                        type="button"
                        /*
                         * A button, not the anchor's `<a>`. These select a slot
                         * on the surface already on screen, so there is no href
                         * to give them, and an `<a>` without one is neither
                         * focusable nor announced as a control.
                         */
                        className={`sh-nv${active ? " sh-on" : ""}`}
                        aria-current={active ? "true" : undefined}
                        onClick={onSelect ? () => onSelect(item.id) : undefined}
                    >
                        {item.icon && <span className="sh-nv__i" aria-hidden="true">{item.icon}</span>}
                        <span>
                            <span className="sh-nv__n">{item.label}</span>
                            {item.sublabel && <span className="sh-nv__s">{item.sublabel}</span>}
                        </span>
                        {/*
                          * Decorative. The state it signals is already carried by
                          * the sublabel wherever there is one, and a bare coloured
                          * dot announced on its own says nothing useful.
                          */}
                        {item.live && <span className="sh-nv__d" aria-hidden="true" />}
                    </button>
                );
            })}
        </nav>
    );
};

export default SurfaceRailNav;
